// @ts-nocheck
import { JxsComponent } from '@jxs/component';

/**
 * @typedef {object} JxsProductMediaTabsRefs
 * @property {HTMLButtonElement} imageTab
 * @property {HTMLButtonElement} videoTab
 *
 * @extends {JxsComponent<JxsProductMediaTabsRefs>}
 */
class JxsProductMediaTabs extends JxsComponent {
  requiredRefs = ['imageTab', 'videoTab'];

  /**
   * @type {'image' | 'video'}
   */
  #currentTab = 'image';

  /**
   * @type {HTMLElement | null}
   */
  #media = null;

  /**
   * @type {AbortController | null}
   */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#media = this.closest('hdt-product-media');
    if (!this.#media) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.applyTab('image', { initial: true });

    const formId = this.#media.getAttribute('form');
    const form = formId ? document.forms[formId] : null;
    form?.addEventListener(
      'variant:change',
      (event) => {
        const mediaId = event.detail?.variant?.mdid;
        queueMicrotask(() => this.applyTab(this.#currentTab, { preferMediaId: mediaId }));
      },
      { signal }
    );

    this.#media.addEventListener(
      'update:value',
      () => {
        queueMicrotask(() => this.applyTab(this.#currentTab));
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {{ tab?: string }} data
   */
  selectTab(data) {
    const tab = data?.tab;
    if (tab !== 'image' && tab !== 'video') return;
    if (tab === this.#currentTab) return;
    this.applyTab(tab);
  }

  /**
   * @param {string} [type]
   */
  isVideoType(type) {
    return type === 'video' || type === 'external_video';
  }

  /**
   * @param {'image' | 'video'} tab
   * @param {{ initial?: boolean, preferMediaId?: string | number }} [options]
   */
  applyTab(tab, options = {}) {
    if (!this.#media) return;

    this.#currentTab = tab;
    this.#media.setAttribute('data-jxs-media-tab', tab);
    this.dataset.activeTab = tab;

    this.refs.imageTab?.setAttribute('aria-selected', tab === 'image' ? 'true' : 'false');
    this.refs.videoTab?.setAttribute('aria-selected', tab === 'video' ? 'true' : 'false');

    const slides = this.#media.querySelectorAll('.hdt-slider__slide');

    slides.forEach((slide) => {
      const type = slide.getAttribute('data-media-type') || 'image';
      const isVideo = this.isVideoType(type);
      const match = tab === 'video' ? isVideo : !isVideo;

      if (match) {
        if (slide.hasAttribute('data-jxs-media-tab-hidden')) {
          if (slide.hasAttribute('data-jxs-was-visible')) {
            slide.removeAttribute('hidden');
          }
          slide.removeAttribute('data-jxs-media-tab-hidden');
          slide.removeAttribute('data-jxs-was-visible');
        }
      } else if (!slide.hasAttribute('data-jxs-media-tab-hidden')) {
        if (!slide.hasAttribute('hidden')) {
          slide.setAttribute('data-jxs-was-visible', '');
        }
        slide.setAttribute('hidden', '');
        slide.setAttribute('data-jxs-media-tab-hidden', '');
      } else {
        slide.setAttribute('hidden', '');
      }
    });

    if (tab !== 'video') {
      this.#media.querySelectorAll('hdt-video-player[loaded]').forEach((player) => {
        player.pause?.();
      });
    }

    const sliderMain = this.#media.querySelector('.hdt-product-media__main hdt-slider');
    const sliderThumb = this.#media.querySelector('hdt-slider-thumb');

    try {
      sliderMain?.reInit?.();
      sliderThumb?.reInit?.();
    } catch (error) {
      console.error(error);
    }

    const firstVisible = this.#media.querySelector(
      '.hdt-product-media__main .hdt-slider__slide:not([hidden])'
    );
    let target = firstVisible;
    const preferMediaId = options.preferMediaId;
    if (preferMediaId) {
      const preferred = this.#media.querySelector(
        `.hdt-product-media__main [data-media-id="${preferMediaId}"]:not([hidden])`
      );
      if (preferred) target = preferred;
    }

    if (target) {
      if (sliderMain?.isActive) {
        sliderMain.goToTarget(target, true);
      } else if (!options.initial) {
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      const mediaId = target.getAttribute('data-media-id');
      if (mediaId && sliderThumb?.isActive) {
        const thumbTarget = this.#media.querySelector(
          `hdt-slider-thumb [data-media-id="${mediaId}"]:not([hidden])`
        );
        if (thumbTarget) sliderThumb.goToTarget(thumbTarget, true);
      }
    }

    const visibleCount = this.#media.querySelectorAll(
      '.hdt-product-media__main .hdt-slider__slide:not([hidden])'
    ).length;
    this.#media.querySelectorAll('[update-total]').forEach((el) => {
      el.textContent = String(visibleCount);
    });
  }
}

customElements.define('jxs-product-media-tabs', JxsProductMediaTabs);
